import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FileText, Globe, Save, X } from 'lucide-react';
import apiService from '../../services/api';
import { directionForLanguage } from '../../i18n/config';
import './AnalyzeAgainModal.css';
import './ClientRequestPreferencesModal.css';

export const CLIENT_REQUEST_SOURCES = {
  THEIR_SIDE_REDLINES: 'their_side_redlines',
  OUR_SIDE_REDLINES: 'our_side_redlines',
  PLAIN_TEXT: 'plain_text',
} as const;

export type ClientRequestSource =
  (typeof CLIENT_REQUEST_SOURCES)[keyof typeof CLIENT_REQUEST_SOURCES];

const SOURCE_VALUES: ClientRequestSource[] = [
  CLIENT_REQUEST_SOURCES.THEIR_SIDE_REDLINES,
  CLIENT_REQUEST_SOURCES.OUR_SIDE_REDLINES,
  CLIENT_REQUEST_SOURCES.PLAIN_TEXT,
];

interface ClientRequestPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialModuleA: string[];
  initialModuleB: string[];
  onSaved?: () => void | Promise<void>;
}

export const ClientRequestPreferencesModal: React.FC<ClientRequestPreferencesModalProps> = ({
  isOpen,
  onClose,
  initialModuleA,
  initialModuleB,
  onSaved,
}) => {
  const { t, i18n } = useTranslation('taskpane');
  const modalDir = directionForLanguage(i18n.language);
  const latinMarks = {
    mod: <bdi dir="ltr" />,
    web: <bdi dir="ltr" />,
    word: <bdi dir="ltr" />,
  };
  const [moduleA] = React.useState<string[]>(
    initialModuleA.length ? initialModuleA : [CLIENT_REQUEST_SOURCES.PLAIN_TEXT],
  );
  const [moduleB, setModuleB] = React.useState<string[]>(initialModuleB);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      setModuleB(
        initialModuleB.length ? initialModuleB : [CLIENT_REQUEST_SOURCES.THEIR_SIDE_REDLINES],
      );
      setError(null);
    }
  }, [isOpen, initialModuleB]);

  if (!isOpen) return null;

  const toggleModuleB = (value: ClientRequestSource) => {
    setModuleB((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleSave = async () => {
    if (moduleB.length === 0) {
      setError(t('profile.clientRequestModal.selectModuleB'));
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await apiService.updateClientRequestPreferences(undefined, moduleB);
      if (onSaved) await onSaved();
      onClose();
    } catch (e: any) {
      setError(
        e?.response?.data?.detail || e?.message || t('profile.clientRequestModal.saveFailed'),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const renderGroup = (
    titleKey: 'profile.clientRequestModal.moduleATitle' | 'profile.clientRequestModal.moduleBTitle',
    icon: React.ReactNode,
    selected: string[],
    disabled: boolean,
    onToggle?: (value: ClientRequestSource) => void,
  ) => (
    <div className={`cr-prefs-group ${disabled ? 'cr-prefs-group-disabled' : ''}`}>
      <div className="cr-prefs-group-header">
        {icon}
        <span className="cr-prefs-group-title">
          <Trans t={t} i18nKey={titleKey} components={latinMarks} />
        </span>
        {disabled && (
          <span className="cr-prefs-group-badge">{t('profile.clientRequestModal.comingSoon')}</span>
        )}
      </div>
      <div className="cr-prefs-options">
        {SOURCE_VALUES.map((value) => {
          const checked = selected.includes(value);
          return (
            <label
              key={value}
              className={`cr-prefs-option ${disabled ? 'cr-prefs-option-disabled' : ''}`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => onToggle && onToggle(value)}
              />
              <span>{t(`profile.sources.${value}`)}</span>
            </label>
          );
        })}
      </div>
    </div>
  );

  return (
    <div
      className="aa-modal-backdrop cr-prefs-modal-backdrop"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isSaving) onClose();
      }}
    >
      <div
        className="aa-modal cr-prefs-modal"
        dir={modalDir}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cr-prefs-modal-title"
      >
        <div className="aa-modal-header">
          <div className="aa-modal-header-text">
            <h2 id="cr-prefs-modal-title" className="aa-modal-title">
              {t('profile.clientRequestModal.title')}
            </h2>
            <p className="aa-modal-subtitle">{t('profile.clientRequestModal.subtitle')}</p>
          </div>
          <button
            type="button"
            className="aa-modal-close"
            onClick={onClose}
            disabled={isSaving}
            aria-label={t('actions.close', { ns: 'common' })}
          >
            <X size={18} />
          </button>
        </div>

        <p className="aa-modal-lead">
          <Trans t={t} i18nKey="profile.clientRequestModal.lead" components={latinMarks} />
        </p>

        {error && <div className="cr-prefs-error">{error}</div>}

        <div className="cr-prefs-groups">
          {renderGroup(
            'profile.clientRequestModal.moduleATitle',
            <Globe size={16} className="cr-prefs-group-icon cr-prefs-group-icon-muted" />,
            moduleA,
            true,
          )}
          {renderGroup(
            'profile.clientRequestModal.moduleBTitle',
            <FileText size={16} className="cr-prefs-group-icon" />,
            moduleB,
            false,
            toggleModuleB,
          )}
        </div>

        <div className="aa-modal-actions cr-prefs-actions">
          <button
            type="button"
            className="aa-btn-secondary"
            onClick={onClose}
            disabled={isSaving}
          >
            {t('actions.cancel', { ns: 'common' })}
          </button>
          <button
            type="button"
            className="aa-btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            <Save size={14} aria-hidden />
            {isSaving ? t('status.saving', { ns: 'common' }) : t('actions.save', { ns: 'common' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientRequestPreferencesModal;
